import {Controller} from "@hotwired/stimulus"
import {placeFloatingPanel} from "../../lib/floating_panel"

// Inline cell editing for redesigned (ice-) tables.
//
// Replaces the Bootstrap-popover based `editable-cell` controller. The popover
// rebuilt its DOM from the original HTML every time it closed, which forced that
// controller to re-sync the <input> on `hidden.bs.popover`. Here the panel is
// plain markup that is only shown and hidden, so there is no DOM to re-sync and
// no Bootstrap JS dependency.
//
// The form itself is unchanged: turboboost submits it and fires
// `jquery:ajax:success` / `jquery:ajax:error`, which map to onSuccess/onError.
//
// The panel is position: fixed and placed from the cell's rect. Absolute
// positioning would be clipped by any ancestor with overflow: hidden, and flipping
// direction does not help against a clip. position() keeps it glued to the cell
// while open, and flips it above the cell on the last rows of the page.

export default class extends Controller {
    static targets = ["display", "panel", "input", "errors"]
    static values = {placement: {type: String, default: "bottom-left auto"}}

    connect() {
        this.onDocumentClick = (event) => {
            if (!this.element.contains(event.target)) this.close()
        }

        this.onDocumentKeydown = (event) => {
            if (event.key === "Escape") this.close()
        }

        // Capture phase so scrolling an ancestor container counts too, not just
        // the document.
        this.onViewportChange = () => this.position()

        document.addEventListener("click", this.onDocumentClick)
        document.addEventListener("keydown", this.onDocumentKeydown)
    }

    disconnect() {
        document.removeEventListener("click", this.onDocumentClick)
        document.removeEventListener("keydown", this.onDocumentKeydown)
        this.stopTrackingViewport()
    }

    toggle(event) {
        event.preventDefault()
        this.isOpen ? this.close() : this.open()
    }

    open() {
        // Only one cell should be editable at a time, otherwise two open panels
        // overlap each other in adjacent columns.
        document.querySelectorAll("[data-inline-edit-open]").forEach((element) => {
            if (element === this.element) return

            const other = this.application.getControllerForElementAndIdentifier(element, "inline-edit")
            if (other) other.close()
        })

        this.element.setAttribute("data-inline-edit-open", "true")
        this.panelTarget.classList.remove("ice:hidden")

        this.position()
        this.startTrackingViewport()

        if (this.hasInputTarget) {
            this.inputTarget.focus()
            this.inputTarget.select()
        }
    }

    close() {
        if (!this.isOpen) return

        this.element.removeAttribute("data-inline-edit-open")
        this.panelTarget.classList.add("ice:hidden")
        if (this.hasErrorsTarget) this.errorsTarget.innerHTML = ""
        this.stopTrackingViewport()
    }

    // Anchored to the trigger, not the cell around it. A table cell is as wide as its
    // column, so anchoring to it put the panel under the far edge of the column
    // rather than under the thing that was clicked.
    position() {
        if (!this.isOpen) return

        const anchor = this.hasDisplayTarget ? this.displayTarget : this.element
        placeFloatingPanel(this.panelTarget, anchor.getBoundingClientRect(), {placement: this.placementValue})
    }

    startTrackingViewport() {
        window.addEventListener("scroll", this.onViewportChange, {passive: true, capture: true})
        window.addEventListener("resize", this.onViewportChange, {passive: true})
    }

    stopTrackingViewport() {
        window.removeEventListener("scroll", this.onViewportChange, {capture: true})
        window.removeEventListener("resize", this.onViewportChange)
    }

    onSuccess(event) {
        if (this.hasErrorsTarget) this.errorsTarget.innerHTML = ""
        this.displayTarget.innerHTML = event.detail.data

        if (this.hasInputTarget) {
            this.inputTarget.value = this.displayTarget.textContent.trim()
        }

        this.close()
    }

    onError(event) {
        if (this.hasErrorsTarget) this.errorsTarget.innerHTML = event.detail.data
    }

    get isOpen() {
        return this.element.hasAttribute("data-inline-edit-open")
    }
}
